import { useState, useRef, useCallback, useEffect } from 'react';
import { Camera, RotateCcw, Check, X, FlipHorizontal } from 'lucide-react';

interface CameraCaptureProps {
  onCapture: (imageData: Blob, filename: string) => void;
  onCancel: () => void;
}

export default function CameraCapture({ onCapture, onCancel }: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const startCamera = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Stop any existing stream
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      const newStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });

      setStream(newStream);

      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
        await videoRef.current.play();
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError('Unable to access camera. Please ensure camera permissions are granted.');
    } finally {
      setIsLoading(false);
    }
  }, [facingMode, stream]);

  useEffect(() => {
    startCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [facingMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const capture = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas size to video size
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Get the image data URL
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setCapturedImage(imageDataUrl);
  }, []);

  const retake = useCallback(() => {
    setCapturedImage(null);
  }, []);

  const confirm = useCallback(async () => {
    if (!capturedImage) return;

    // Convert data URL to Blob
    const response = await fetch(capturedImage);
    const blob = await response.blob();

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `scan_${timestamp}.jpg`;

    onCapture(blob, filename);
  }, [capturedImage, onCapture]);

  const toggleCamera = useCallback(() => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    setCapturedImage(null);
  }, []);

  if (error) {
    return (
      <div className="bg-white rounded-2xl p-8 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Camera className="w-8 h-8 text-red-500" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">Camera Error</h3>
        <p className="text-slate-600 mb-4">{error}</p>
        <div className="flex justify-center space-x-3">
          <button
            onClick={startCamera}
            className="px-4 py-2 bg-shuttle-primary text-white rounded-lg hover:bg-shuttle-dark transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black rounded-2xl overflow-hidden relative">
      {/* Hidden canvas for capturing */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Video preview or captured image */}
      <div className="relative aspect-[3/4] sm:aspect-video bg-black">
        {capturedImage ? (
          <img
            src={capturedImage}
            alt="Captured"
            className="w-full h-full object-contain"
          />
        ) : (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            
            {/* Viewfinder overlay */}
            <div className="absolute inset-4 sm:inset-8 pointer-events-none">
              <div className="viewfinder-overlay">
                <div className="viewfinder-corner viewfinder-corner-tl" />
                <div className="viewfinder-corner viewfinder-corner-tr" />
                <div className="viewfinder-corner viewfinder-corner-bl" />
                <div className="viewfinder-corner viewfinder-corner-br" />
              </div>
            </div>

            {/* Loading indicator */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Controls */}
      <div className="bg-slate-900 p-4">
        {capturedImage ? (
          <div className="flex justify-center space-x-4">
            <button
              onClick={retake}
              className="flex items-center space-x-2 px-6 py-3 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
              <span>Retake</span>
            </button>
            <button
              onClick={confirm}
              className="flex items-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-500 transition-colors"
            >
              <Check className="w-5 h-5" />
              <span>Use Photo</span>
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <button
              onClick={onCancel}
              className="p-3 bg-slate-700 text-white rounded-full hover:bg-slate-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>

            <button
              onClick={capture}
              disabled={isLoading}
              className="w-16 h-16 bg-white rounded-full flex items-center justify-center hover:bg-slate-200 transition-colors disabled:opacity-50 animate-pulse-glow"
            >
              <div className="w-14 h-14 bg-white rounded-full border-4 border-slate-900" />
            </button>

            <button
              onClick={toggleCamera}
              className="p-3 bg-slate-700 text-white rounded-full hover:bg-slate-600 transition-colors"
            >
              <FlipHorizontal className="w-6 h-6" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}





